// What fills the pop-out window: the other people in small tiles, and below
// them a read-only view of the Breeze cards (tap one to copy it). It is a
// portal into the pop-out's own document, so it shares this page's state and
// the video elements are new ones on the same tracks.
//
// Privacy: while this person shares their screen, the share picker's choice
// decides what shows. A window or a tab cannot include the pop-out, so the
// cards are safe. The whole screen can, and so can a browser that does not
// say what was picked: then only the participants show, with one line why.

import { useMemo, useState, type JSX } from "react";
import { createPortal } from "react-dom";
import type { AssistantStore } from "../hooks/useAssistant";
import type { PanelStore } from "../hooks/usePanel";
import type { RemoteTile } from "../types";
import { stripCards, surfaceKeepsPrivate, surfaceNotice, type ShareSurface } from "@breeze/core";
import { LABELS, metaOf } from "./BreezeStrip";
import { VideoView } from "./VideoView";
import { BreezeMarkIcon, MicOffIcon } from "./icons";

interface PopoutProps {
  readonly root: HTMLElement;
  readonly tiles: readonly RemoteTile[];
  readonly panel: PanelStore;
  readonly assistant: AssistantStore;
  readonly isHost: boolean;
  /** Set while this person shares their screen: what they picked to share. */
  readonly surface: ShareSurface | null;
}

export function Popout({ root, tiles, panel, assistant, isHost, surface }: PopoutProps): JSX.Element {
  const showCards = surfaceKeepsPrivate(surface);
  const notice = surfaceNotice(surface);
  const say = isHost && assistant.state?.on === true ? (assistant.host?.say ?? null) : null;
  const cards = useMemo(
    () =>
      showCards
        ? stripCards({ isHost, sharingScreen: false, shared: panel.shared, priv: panel.priv, suggestions: panel.suggestions, say })
        : [],
    [showCards, isHost, panel.shared, panel.priv, panel.suggestions, say],
  );
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (id: string, text: string): void => {
    const done = (): void => {
      setCopied(id);
      window.setTimeout(() => setCopied((c) => (c === id ? null : c)), 1400);
    };
    // The pop-out's own clipboard first; the call window's if it refuses.
    const own = root.ownerDocument.defaultView?.navigator.clipboard;
    const attempt = (clip: Clipboard | undefined): Promise<void> => (clip ? clip.writeText(text) : Promise.reject(new Error("no clipboard")));
    attempt(own)
      .catch(() => attempt(navigator.clipboard))
      .then(done)
      .catch(() => {});
  };

  return createPortal(
    <div className="pop" data-testid="popout">
      <div className="pop__people" data-testid="popout-people">
        {tiles.length === 0 ? (
          <p className="pop__quiet">Waiting for others to join.</p>
        ) : (
          tiles.map((tile) => (
            <div key={tile.participant.id} className="pop__tile" data-testid="popout-tile">
              {tile.videoTrack && !tile.participant.cameraOff ? (
                <VideoView track={tile.videoTrack} fit="cover" muted />
              ) : (
                <span className="pop__initial">{(tile.participant.name.trim()[0] ?? "?").toUpperCase()}</span>
              )}
              <span className="pop__name">
                {tile.participant.name}
                {tile.participant.muted ? <MicOffIcon size={12} /> : null}
              </span>
            </div>
          ))
        )}
      </div>

      {notice ? (
        <p className="pop__notice" role="status" data-testid="popout-notice">
          {notice}
        </p>
      ) : (
        <section className="pop__cards" aria-label="Breeze" data-testid="popout-cards">
          <h2 className="pop__head">
            <BreezeMarkIcon size={16} />
            Breeze
          </h2>
          {panel.shared === null ? (
            <div className="bpstrip__loading" role="status" aria-label="Loading">
              <span />
              <span />
            </div>
          ) : cards.length === 0 ? (
            <p className="pop__quiet">Nothing in Breeze yet.</p>
          ) : (
            cards.map((card) => (
              <button key={card.id} type="button" className="pop__card" data-testid="popout-card" onClick={() => copy(card.id, card.text)}>
                <span className="pop__label">{LABELS[card.kind]}</span>
                <span className="pop__text">{card.text}</span>
                <span className="pop__meta">{copied === card.id ? "Copied" : metaOf(card)}</span>
              </button>
            ))
          )}
        </section>
      )}
    </div>,
    root,
  );
}
